import { useContext, useEffect, useRef } from 'react';

//api
import { AddEditAPIMap, removeAllPermissions, removeGroupPermissions, removeUserPermissions, resetToParentPermissions, setGroupPermissions, setUserPermissions } from '@/api/contentAudience';

//interfaces
import { AudienceModalState, ResetAudienceOption } from  '@/interfaces/ContentAudienceModal';
import { ITableContent, ITableRows, } from  '@/interfaces/simpleTable';
import {  IGroups, IUserOrGroup, IUsers,  } from  '@/interfaces/api/ContentAudience';
import { ISelectButton } from  '@/interfaces/selectButtonList';
import { ResourcePermission } from  '@/interfaces/orgSettings';
import { PaginatedResponse } from  '@/interfaces/api/PaginatedResponse';
import { ManagementType } from  '@/interfaces/ExternalProvider';

//components
import ContentAudienceModalProfilePics from './ContentAudienceModalProfilePics';
import EditPermissionsControl from './EditPermisssionsControl';
import ActionsControl from './ActionsControl';
import AssignRoleControl from './AssignRoleControl';
import { ToastMessageType } from '../global/ToastNotification';
import ViewPermissionsControl from './ViewPermissionsControl/ViewPermissionsControl';

//context
import { ContentAudienceContext } from './ContentAudienceContext';

// helpers and utils
import { isOrganisationAdmin } from  '@/helpers/OrgSettings';
import { getAppGuid } from '../search/utility';


const useContentAudience = () => {
    
    const recentlyAdded = useRef<{users: IUsers[], groups:IGroups[]}> ({users: [], groups: []})
 
    const {
        contentId,
        filterOptions,
        debouncedSearchTerm,
        sort,
        currentSelection,
        audienceModalState,
        setSort,
        setTotalRecords,
        setCurrentSkip,
        currentSkip,
        table,
        setTable,
        setToast,
        audienceCounts,
        usersOrGroupsLoading,
        setUsersOrGroupsLoading,
        groupFilters,
        isEditor,
        isOrgAdmin,
        updatingUserOrGroupIds,
        setUpdatingUserOrGroupIds,
        getAudienceCounts,
        list,
        setList,
        setDisableModal,
    } = useContext(ContentAudienceContext)


    useEffect(() => {
        recentlyAdded.current = {groups: [], users: []}
    },[audienceModalState])

    useEffect(() => {
        if(!usersOrGroupsLoading) {
            setTable(formatTable(audienceModalState as AudienceModalState, currentSelection, list) as ITableContent)
        }
    },
    [list, usersOrGroupsLoading])

    const isArchivedFilterOn = filterOptions.includes('includeInactive')

    async function updateAudienceResponse(
        modalState: AudienceModalState.AddAudience | AudienceModalState.EditAudience,
        userOrGroupsSelection: 'Groups' | 'Users',
        type: 'paginate' | 'refresh' | 'newtab' |'new' = 'new'
    ): Promise<void>{
        setUsersOrGroupsLoading(true)
        const endpoint = AddEditAPIMap?.[modalState]?.[userOrGroupsSelection];
        endpoint?.(
            contentId,
            {
                filter: type === 'newtab' ? undefined: filterOptions,
                groupIds: type === 'newtab' ? undefined: groupFilters,
                searchTerm: debouncedSearchTerm && type !== 'newtab' ? debouncedSearchTerm : undefined, 
                sort: sort.sort, 
                sortBy: sort.sortBy,
                top: type === 'refresh' ? list.length + recentlyAdded.current[userOrGroupsSelection.toLowerCase() as 'users' | 'groups'].length : 20,
                skip: type === 'paginate' ? currentSkip : 0
            }
        ).then((data: PaginatedResponse<IGroups | IUsers>) => {
            setTotalRecords(data.total)
            // getting an odd top can throw off pagination so best to just not set it if we're doing a content refresh
            if(type !== 'refresh') {
                setCurrentSkip(type === 'paginate' ? [...list, ...data.records].length : data.records.length);
            }
            setList(type === 'paginate' ? [...list, ...data.records] : data.records)
        })
        .catch((error: { status: number; }) => {
            if (error.status === 403) {
                setToast({
                    type: ToastMessageType.WARNING,
                    message: "You no longer have editor access to this content."
                });
                setDisableModal(true);
                setTimeout(() => {
                    window.location.replace(`/a/${getAppGuid()}`);
                }, 1000); 
            }
        })
        .finally(() => {
            setUsersOrGroupsLoading(false);
            getAudienceCounts();
            return Promise.resolve();
        })
    }

    function updateAudienceAndList(){
        getAudienceCounts();
        (window as any)?.functions?.refreshMainMenu?.() 
    }

    const lastPaginatedResponse = useRef<string>('');

    function getNextPage(){
        if(currentSelection === lastPaginatedResponse.current) {
            updateAudienceResponse(
                audienceModalState as AudienceModalState.EditAudience | AudienceModalState.AddAudience,
                currentSelection,
                'paginate'
            )
        }
        else {
            lastPaginatedResponse.current = currentSelection
        }
    }

    async function refreshAudienceList(){
        return updateAudienceResponse(
            audienceModalState as AudienceModalState.EditAudience | AudienceModalState.AddAudience,
            currentSelection,
            'refresh'
        )
    }

    function formatTable(modalState: AudienceModalState, userOrGroupsSelection: 'Groups' | 'Users', list:IGroups[] | IUsers[]){
        if(AudienceModalState.AddAudience === modalState) {
            list = [
                ...(userOrGroupsSelection === 'Groups' ? recentlyAdded.current.groups : recentlyAdded.current.users) as any[],
                ...list
            ]
        }

        const newRows = userOrGroupsSelection === 'Groups'
            ? mapListGroupsToTableRows(list as IGroups[], modalState === AudienceModalState.EditAudience).filter(row => row)
            : mapListUsersToTableRows(list as IUsers[]).filter(row => row)

        return {
            columns: getTableHeadings(userOrGroupsSelection, modalState).filter(heading => heading),
            rows: newRows
        }
    }

    function addToRecentlyAdded({group, permission}: {group?:IGroups, permission: ResourcePermission}){
        
        setUpdatingUserOrGroupIds([...updatingUserOrGroupIds, Number(group?.id)]); 
        setGroupPermissions(Number(contentId), Number(group?.id), permission)
        .then(async () => {
            await refreshAudienceList();
            const currentGroups:IGroups[] = recentlyAdded?.current?.groups ? recentlyAdded.current.groups : []
            recentlyAdded.current = {...recentlyAdded.current, groups: [...currentGroups, group as IGroups]}
            setToast({
                type: ToastMessageType.SUCCESS,
                message: getAddPermissionNotificationDetails(true, permission, String(group?.name), true)
            })
        })
        .catch(() => {
                setToast({
                    type: ToastMessageType.WARNING,
                    message: getAddPermissionNotificationDetails(true, permission, String(group?.name), false)
                });
            }
        )
        .finally(() => {
            setUpdatingUserOrGroupIds(updatingUserOrGroupIds.filter(id => id!== group?.id)); 
            updateAudienceAndList();
        })
        
    }

    function removeRecentlyAdded({group}: {group?:IGroups}){
        const removeEndpoint = removeGroupPermissions
        const { id, name } = {id: Number(group?.id), name: group?.name }
        
        setUpdatingUserOrGroupIds([...updatingUserOrGroupIds, id])
        
        removeEndpoint(Number(contentId), id)
        .then(async () => {
            await refreshAudienceList();
            recentlyAdded.current = {...recentlyAdded.current, groups: recentlyAdded.current.groups.filter(g => g.id !== Number(group?.id))}
            
            setToast({
                type: ToastMessageType.SUCCESS,
                message: `Removed ${name} from audience`
            })
        })
        .catch(() => {
            setToast({
                type: ToastMessageType.WARNING,
                message: `Could not removed ${name} from audience at this time`
            })
        })
        .finally(() => {
            setUpdatingUserOrGroupIds(updatingUserOrGroupIds.filter(i => i !== id))
            updateAudienceAndList();
        });
    }

    function getTableHeadings( userOrGroupsSelection: 'Groups' | 'Users', modalState:AudienceModalState) {
        let columns;
        
        if (userOrGroupsSelection === 'Groups') {
            columns = [
                {title: 'Group', isToggleButton:true, onSortAscendingClick:() => setSort({sort:'asc', sortBy:'groupsName'}), onSortDescendingClick: () => setSort({sort:'desc', sortBy:'groupsName'}), defaultSort: audienceModalState === AudienceModalState.AddAudience && 'asc'},
                {title: 'Members', isToggleButton:true, onSortAscendingClick:() => setSort({sort:'asc', sortBy:'members'}), onSortDescendingClick: () => setSort({sort:'desc', sortBy:'members'})},
                {title: 'Status'},
                {title: 'Permissions', isToggleButton: modalState === AudienceModalState.EditAudience, onSortAscendingClick:() => setSort({sort:'asc', sortBy:'permissions'}), onSortDescendingClick: () => setSort({sort:'desc', sortBy:'permissions'}), defaultSort: audienceModalState === AudienceModalState.EditAudience && 'desc'},
                modalState === AudienceModalState.EditAudience ? {title: 'Actions', isToggleButton:false} : undefined
            ]
        } else {
            columns = [
                {title: 'User', isToggleButton:true, onSortAscendingClick:() => setSort({sort:'asc', sortBy:'userName'}), onSortDescendingClick: () => setSort({sort:'desc', sortBy:'userName'}) },
                {title: 'Permissions', isToggleButton:true, onSortAscendingClick:() => setSort({sort:'asc', sortBy:'permissions'}), onSortDescendingClick: () => setSort({sort:'desc', sortBy:'permissions'}), defaultSort:'desc'},
                {title: 'Status'},
                {title: 'Actions', isToggleButton:false}
            ]
        }
        return columns.filter(val => val);
    }

    function mapListUsersToTableRows (list: IUsers[]):ITableRows[] {
        return list.map(user => {
            return {
                content:[
                    <ContentAudienceModalProfilePics 
                        key={user.id} 
                        name={user.userName} 
                        profilePics={[user.profilePictureUrl]} 
                        cap={1} 
                        type={'users'} 
                        isExternallyManaged={user.userManagementType === ManagementType.external} 
                        externalLoginIconUrl={user.provider?.iconUrl}
                    />,
                    <ViewPermissionsControl
                        key={`${user.id}-permission-control`}
                        user={user}
                    />,
                    <span key={`${user.id}-status`} className={'status-field'}>{getStatusString(user.status)}</span>,
                    <ActionsControl key={`${user.id}-action-control`} isSingleUser={true} id={user.id} name={user.userName} isAdmin={isOrganisationAdmin(user.permission, user.grantType)}/>    
                ].filter(c => c),
            }
        })
    } 

    function getStatusString(groupStatus: 'archive' | 'active') {
        return groupStatus === 'archive' ? 'Archived' : 'Active'
    }
    
    function mapListGroupsToTableRows (list: IGroups[], isEdit: boolean): ITableRows[] {
        return list.map(group => {
            return {
                content:[
                    <ContentAudienceModalProfilePics 
                        key={`${group.id}-profile-pics`}
                        name={group.name}
                        profilePics={group.profilePictureUrls as any[]}
                        cap={4}
                        type={'groups'}
                        hasNoUsers={group.totalMembers === 0}
                        isExternallyManaged={group.groupManagementType === ManagementType.external}
                        externalLoginIconUrl={group.provider?.iconUrl}
                    />,
                    <span key={`${group.id}-total-members`}>{group.totalMembers}</span>,
                    <span key={`${group.id}-status`} className='status-field'>{getStatusString(group.status)}</span>,
                    isEdit 
                        ? <EditPermissionsControl
                            key={`${group.id}-edit-permissions`}
                            group={group}
                            setAudiencePermission={setAudiencePermission}
                        />
                        : <AssignRoleControl 
                            key={`${group.id}-role-control`}
                            userOrGroup={group}
                            addToRecentlyAdded={(group: IUserOrGroup, permission) =>  addToRecentlyAdded({group: group as IGroups, permission})}
                            removeRecentlyAdded={(group: IUserOrGroup) =>  removeRecentlyAdded({group: group as IGroups})}
                        />,
                    isEdit 
                    ? <ActionsControl 
                        key='action-control'
                        isSingleUser={false}
                        id={group.id}
                        name={group.name}
                        isAdmin={isOrganisationAdmin(group.permission, group.grantType)}
                        isLoading={!!updatingUserOrGroupIds.length}
                    />
                    : undefined
                ].filter(c => c),
            }
        })
    }

    function setAudiencePermission(isGroup: boolean, name: string, id:number,  permission: ResourcePermission) {
        
        const endpoint = isGroup ? setGroupPermissions : setUserPermissions
        setUpdatingUserOrGroupIds([...updatingUserOrGroupIds, id])
        endpoint(Number(contentId), id, permission)
        .then(async () => {
            await refreshAudienceList();
            setToast({
                type: ToastMessageType.SUCCESS,
                message: getAddPermissionNotificationDetails(isGroup, permission, name, true)
            })
        })
        .catch(() => {
                setToast({
                    type: ToastMessageType.WARNING,
                    message: getAddPermissionNotificationDetails(isGroup, permission, name, false)
                })
            
        })
        .finally(() => {
            setUpdatingUserOrGroupIds(updatingUserOrGroupIds.filter(i => i !== id)); 
            updateAudienceAndList();
        })
    }

    function getPermissionString(isGroup: boolean, permission: ResourcePermission){
        if (isGroup) {
            return permission === ResourcePermission.MANAGE ? "Editors" : "Viewers";
        } else {
            return permission === ResourcePermission.READ ? "an Editor" : "a Viewer";
        }
    }

    function getAddPermissionNotificationDetails(isGroup:boolean, permission:ResourcePermission, name:string, success: boolean){
        let permissionString  = getPermissionString(isGroup, permission);

        if(success){
            return  `Added ${name} as ${permissionString}`
            
        } 
        return `Could not add ${name} as ${permissionString} at this time`
    }

    

    function handlePermissionsReset(resetType: ResetAudienceOption, successCb?: () => void, failCb?:() => void) {
        const reset = resetType === ResetAudienceOption.RemoveAll
            ? removeAllPermissions
            : resetToParentPermissions;

        reset(contentId)
        .then(() => {
            successCb?.()
        })
        .catch(() => {
            failCb?.()
        })
    }

    function getNoDataMessage(): string{
        if(usersOrGroupsLoading) {
            return `fetching ${currentSelection.toLocaleLowerCase()}...`
        }
        return audienceModalState === AudienceModalState.AddAudience
            ? `There are no additional ${currentSelection.toLocaleLowerCase()} that can be granted access to this content`
            : `No ${currentSelection.toLocaleLowerCase()} have been granted access to this content`
    }

    function removeFromAudience(isUser: boolean, id: number, name: string): void {
        const endpoint = isUser ? removeUserPermissions : removeGroupPermissions

        setUpdatingUserOrGroupIds([...updatingUserOrGroupIds, id])

        endpoint(Number(contentId), id)
        .then(async () => {
            await refreshAudienceList();
            setToast({
                type: ToastMessageType.SUCCESS,
                message: `Removed ${name} from audience`
            })
        })
        .catch(() => {
            setToast({
                type: ToastMessageType.WARNING,
                message: `Could not removed ${name} from audience at this time`
            })
        })
        .finally(() => {
            setUpdatingUserOrGroupIds(updatingUserOrGroupIds.filter(i => i !== id));
            updateAudienceAndList();
        });
    }

    function getActionMenuButtons(id: number, name: string, userIsAdmin: boolean){
        const buttons: ISelectButton[] = []

        const isGroup = currentSelection === "Groups"

        if(isOrgAdmin) {
            const href = isGroup
                ? `${window.location.origin}/admin/OrganizationUserGroup/UserGroupDetail/${id}`
                : `${window.location.origin}/admin/OrganizationUser/EditProfile/${id}`
            buttons.push({
                content: <a href={href} target='_blank'> Manage  {isGroup ? 'Group' : 'User'}</a>, 
                onClick: (e) => {
                    if((e?.target as HTMLElement).tagName === 'A') {
                        e?.preventDefault();
                    }
                    window.open(href, '_blank');
                }
            })
        }
        if (isGroup && audienceModalState === AudienceModalState.EditAudience && !userIsAdmin) {
            buttons.push({
                content: <span>Remove from audience </span>, 
                onClick: () => removeFromAudience(false, id, name)  
            })
        }

        return buttons
    }

    return {
        audienceCounts,
        getAudienceCounts, 
        updateAudienceResponse,
        table,
        handlePermissionsReset,
        getNoDataMessage,
        getNextPage,
        removeFromAudience,
        isEditor,
        isOrgAdmin,
        getActionMenuButtons,
        isArchivedFilterOn
    };
}

export default useContentAudience;